/**
 * AddToCalendarDropdown - Dropdown menu for adding events to calendar
 * 
 * Provides options to:
 * - Add to Google Calendar (opens in new tab)
 * - Download .ics file (for Apple Calendar, Outlook, etc.)
 * 
 * Both options use shared utilities from calendarExport.ts for consistent
 * event title, description, and location formatting.
 * 
 * @module components/calendar/AddToCalendarDropdown
 */

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CalendarPlus, FileDown, ExternalLink } from 'lucide-react';
import { 
  CalendarEventData, 
  buildGoogleCalendarUrl, 
  downloadICSFile 
} from '../../utils/calendarExport';

// Re-export the type for consumers
export type { CalendarEventData };

export interface AddToCalendarDropdownProps {
  /** Event data for calendar generation */
  event: CalendarEventData;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Additional className */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

const AddToCalendarDropdown: React.FC<AddToCalendarDropdownProps> = ({
  event,
  size = 'sm',
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 224; // w-56 = 14rem = 224px
      
      // Position dropdown below button, aligned to right edge
      setDropdownPosition({
        top: rect.bottom + 4, // 4px gap below button
        left: rect.right - dropdownWidth, // Align right edges
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on scroll (optional, helps with fixed positioning)
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) setIsOpen(false);
    };

    if (isOpen) {
      window.addEventListener('scroll', handleScroll, true);
    }
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const handleGoogleCalendar = () => {
    const url = buildGoogleCalendarUrl(event);
    window.open(url, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  const handleDownloadICS = () => {
    downloadICSFile(event);
    setIsOpen(false);
  };

  const buttonSizeClasses = size === 'sm' 
    ? 'p-1.5' 
    : 'p-2';

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';

  // Render dropdown in a portal to escape overflow constraints
  const dropdownMenu = isOpen ? createPortal(
    <div 
      ref={dropdownRef}
      className="fixed w-56 bg-white dark:bg-background-dark-secondary rounded-lg shadow-lg border border-border-light dark:border-border-dark z-[9999]"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
      }}
      role="menu"
    >
      <div className="p-1">
        {/* Google Calendar option */}
        <button
          onClick={handleGoogleCalendar}
          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded-md transition-colors"
          role="menuitem"
        >
          <ExternalLink className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
          <span>Add to Google Calendar</span>
        </button>

        {/* Divider */}
        <div className="my-1 border-t border-border-light dark:border-border-dark" />

        {/* ICS download option */}
        <button
          onClick={handleDownloadICS}
          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded-md transition-colors"
          role="menuitem"
        >
          <FileDown className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
          <span>Download .ics file</span>
        </button>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`${buttonSizeClasses} text-text-tertiary hover:text-primary-600 dark:text-text-dark-tertiary dark:hover:text-primary-400 transition-colors rounded hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary`}
        title="Add to Calendar"
        aria-label="Add to Calendar"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <CalendarPlus className={iconSize} />
      </button>

      {/* Dropdown rendered via portal */}
      {dropdownMenu}
    </div>
  );
};

export default AddToCalendarDropdown;
